import { useEffect, useState, type CSSProperties } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { AlertTriangle, Check, Mail, X } from "lucide-react";
import type { GameInvite } from "../../types/protocol";
import { Avatar } from "../ui/Avatar";

/** Fenêtre d'invitation typique : quelques minutes, on affiche mm:ss. */
const countdownLabel = (remaining: number) => {
  const seconds = Math.max(0, Math.ceil(remaining / 1_000));
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(seconds % 60).padStart(2, "0")}`;
};

interface InviteToastProps {
  invites: GameInvite[];
  /** Accepter en pleine partie fait quitter la table : on demande confirmation. */
  inGame: boolean;
  pending: string | null;
  onAccept: (invite: GameInvite) => void;
  onDecline: (invite: GameInvite) => void;
}

export const InviteToast = ({ invites, inGame, pending, onAccept, onDecline }: InviteToastProps) => {
  const [now, setNow] = useState(() => Date.now());
  const [confirming, setConfirming] = useState<string | null>(null);

  useEffect(() => {
    if (invites.length === 0) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, [invites.length]);

  return (
    <div className="invite-stack" aria-live="polite">
      <AnimatePresence initial={false}>
        {invites.map((invite) => {
          const created = Date.parse(invite.createdAt);
          const expires = Date.parse(invite.expiresAt);
          const remaining = Number.isNaN(expires) ? 0 : Math.max(0, expires - now);
          // Une date illisible ne doit pas vider la barre : on la laisse pleine.
          const total = Number.isNaN(created) ? remaining || 1 : Math.max(1, expires - created);
          const busy = pending === `invite-respond:${invite.id}`;
          const needsConfirmation = inGame && confirming !== invite.id;

          return (
            <motion.article
              className="invite-card glass-panel"
              key={invite.id}
              layout
              initial={{ opacity: 0, y: -14, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.98 }}
              transition={{ type: "spring", stiffness: 280, damping: 26 }}
            >
              <span
                className="invite-card__timer"
                style={{ "--invite-progress": remaining / total } as CSSProperties}
                aria-hidden="true"
              />
              <header className="invite-card__head">
                <Avatar profile={invite.from} size="md" />
                <span className="invite-card__identity">
                  <small><Mail size={12} /> Invitation à jouer</small>
                  <strong>{invite.from.displayName}</strong>
                  <span>Table <b>{invite.roomCode}</b> · expire dans {countdownLabel(remaining)}</span>
                </span>
                <button
                  className="icon-button"
                  type="button"
                  onClick={() => onDecline(invite)}
                  disabled={busy}
                  aria-label={`Refuser l’invitation de ${invite.from.handle}`}
                >
                  <X size={17} />
                </button>
              </header>

              {inGame && (
                <p className="invite-card__warning">
                  <AlertTriangle size={13} aria-hidden="true" />
                  Rejoindre cette table te fera quitter celle où tu joues actuellement.
                </p>
              )}

              <div className="invite-card__actions">
                <button
                  className="primary-button primary-button--small"
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    if (needsConfirmation) {
                      setConfirming(invite.id);
                      return;
                    }
                    onAccept(invite);
                  }}
                  aria-label={
                    needsConfirmation
                      ? `Quitter la partie et rejoindre la table de ${invite.from.displayName}`
                      : `Accepter l’invitation de ${invite.from.handle}`
                  }
                >
                  {busy ? <span className="button-spinner" aria-hidden="true" /> : <Check size={15} />}
                  {needsConfirmation ? "Quitter et rejoindre" : confirming === invite.id ? "Confirmer" : "Accepter"}
                </button>
                <button
                  className="ghost-button ghost-button--compact"
                  type="button"
                  disabled={busy}
                  onClick={() => onDecline(invite)}
                >
                  Refuser
                </button>
              </div>
            </motion.article>
          );
        })}
      </AnimatePresence>
    </div>
  );
};
